import { rcEnums } from 'types/rc.enums';
import { RCFacility, RCActivityTime } from './Venue';
import { RCMediaObject } from './Media';
import { RCAddress } from './Address';
import { RCEvent } from './Event';
import { RCTeam } from './Team';
import { RCUser, RCCreator } from './User';
import { RCWebPortal, RCPaymentSettingStatus } from './Organization';
import { MediaInterface } from 'types/media';

export enum RCLeagueDetailTypeEnum {
	OTHER = 1,
	MINAGE = 2,
	MAXAGE = 3,
	GENDER = 4,
	LEVELOFPLAY = 5,
	GAMESSEASON = 6,
	MINWEEK = 7,
	SURFACE = 8,
	FORMAT = 9,
	PLAYERSPERTEAM = 10,
	MATCHLENGTH = 12,
}

export enum RCLeagueSeasonStatusEnum {
	UNPUBLISHED = 1,
	PUBLISHED = 2,
	CLOSED = 3,
	CANCELLED = 4,
}

export interface IRCLeagueDetailData {
	detailType: RCLeagueDetailTypeEnum;
	title?: string;
	ordinal?: number;
	data: string | number[];
}

export interface IRCLeagueDetail {
	id?: number;
	leagueId?: number;
	leagueDetails: IRCLeagueDetailData[];
	title?: string;
	ordinal?: number;
	createdAt?: string;
	updatedAt?: string;
	data?: unknown;
	isCustom?: boolean;
}

export enum RCLeagueBookingStateEnum {
	BOOK_BY_EMAIL = 1,
	BOOK_DIRECTLY = 2,
	NO_BOOKING = 3,
	REDIRECT = 4,
}

export enum RCEventMatchStatusEnum {
	PENDING = 1,
	ACTIVE = 2,
	CANCELLED = 3,
}

export interface RCMatchParticipants {
	id?: number;
	matchId?: number;
	ordinal?: number;
	outcomeOrdinal?: number;
	resultMetaData?: unknown;
	points?: number;
	gameSlotId?: number;
	participants?: number;
	participantId?: number;
	score?: number;
}

export interface RCSeasonEventMatchData {
	id?: number;
	status?: RCEventMatchStatusEnum;
	eventId?: number;
	startDate?: Date;
	endDate?: Date;
	startTime?: string;
	endTime?: string;
	participants?: Array<{
		id?: number;
		score?: number;
		points?: number;
		entity?: {
			id: number;
			entityType: rcEnums.shared.ResourceNameTypeEnum;
			name: string;
			logo?: RCMediaObject;
			mainMedia?: {
				url: string;
			};
		};
	}>;
}

export interface RCSeasonRoundMatch extends RCEvent {
	startDateString: string;
	endTime: string;
	startTime: string;
	venueName?: string;
	match: RCSeasonEventMatchData;
	requestsStatus?: {
		valid: boolean;
		reasons?: {
			participantId: number;
			participantName: string;
			participantType: rcEnums.shared.ResourceNameTypeEnum;
		}[];
	};
}

export interface RCSeasonSeries {
	id: number;
	isBye: boolean;
	looseSeriesId: number;
	ordinal: number;
	roundId: number;
	tournamentId: number;
	winSeriesId: number;
	matches: RCSeasonRoundMatch[];
	seriesParticipants: RCMatchParticipants[];
}

export interface RCSeasonRound {
	id: number;
	name: string;
	ordinal: number;
	seasonId: number;
	createdAt: string;
	matches: RCSeasonRoundMatch[];
	series?: RCSeasonSeries[];
	divisionId?: number;
}

export enum RCCanJoinSeasonEnum {
	CAN_JOIN = 0,
	CANNOT_ALREADY_IN_SEASON = 1,
	CANNOT_MISSING_ORGANIZATION_SUBSCRIPTION = 2,
	CANNOT_SEASON_STATUS = 3,
}

export enum RCSeasonCanJoinStatus {
	REGISTRATION_OPEN_NOW_PLAYING = 'registration-open-now-playing',
	REGISTRATION_OPEN = 'registration-open',
	NOW_PLAYING = 'now-playing',
	REGISTRATION_ENDED = 'registration-ended',
	ENDED = 'ended',
}

export enum RCSeasonScheduleStatusEnum {
	DRAFT = 'draft',
	PUBLISHED = 'published',
}

export enum RCSeasonRosterStatusEnum {
	DRAFT = 'draft',
	PUBLISHED = 'published',
}

export interface RCVenueActivityItem {
	address?: RCAddress;
	addressId?: number;
	activityTime: RCActivityTime;
	concurrent?: number;
	venueId?: number;
	venueName?: string;
	seasonId?: number;
	id?: number;
	packageStartHours?: string[];
}

export interface RCUserInvite {
	id: number;
	token: string;
	invitedUserEmail: string;
	invitedUserPhone: string;
	invitedUserId: number;
	createdLink: string;
	name: string;
	userCreatorId: number;
	createdAt: string;
	status?: number;
	user?: RCUser;
	creatorType?: string;
	creator?: RCCreator;
	creatorId?: number;
}

export interface RCSeasonTeamRequest {
	dayOfWeek: number;
	open: string;
	close: string;
	requestType: 'can' | 'cant';
}

export interface RCSeasonTeam {
	id: number;
	seasonId: number;
	team: RCTeam;
	teamId: number;
	userInvites: RCUserInvite[];
	divisionId?: number;
	metaData?: {
		scheduleRequests?: RCSeasonTeamRequest[];
	};
	captain?: RCUser;
}

export interface RCSeasonDivision {
	id?: number;
	name: string;
	ordinal: number;
	color: string;
	rounds?: RCSeasonRound[];
	seasonTeams?: RCSeasonTeam[];
}

export interface RCStanding extends RCSeasonTeam {
	id: number;
	points: number;
	seasonId: number;
	standingPosition: number;
	statistics?: {
		loses: number;
		wins: number;
		winPercentage: number;
		gamesPlayed: number;
		ties?: number;
		overTime?: number;
		againstTeam: number;
		differenceTeam: number;
		forTeam: number;
	};
	team: RCTeam;
	teamId: number;
}

export interface RCSeasonDivisionStanding {
	id?: number;
	name: string;
	ordinal: number;
	color: string;
	rounds?: RCSeasonRound[];
	seasonTeams?: RCStanding[];
}

export interface RCLeagueSeasonSignObject {
	memberSign?: boolean;
	signType?: 'single' | 'group' | 'team' | 'teamMember';
	paymentId?: number;
	nonce?: string;
	entityId?: number;
	installmentsAmount?: number;
	cashPayment?: boolean;
	answers?: {
		questionId: number;
		value: { value?: unknown } | unknown;
	}[];
}

export interface ISeasonMetadata {
	waiverSign?: boolean;
	shirtSize?: string;
	shirtNumber?: string;
	shirtColor?: string;
	additionalData?: string;
}

export interface SeasonPoolInstance {
	id?: number;
	seasonId?: number;
	userId?: number;
	status?: rcEnums.league.SeasonPoolStatusEnum;
	entityId?: number;
	entityType?: rcEnums.shared.ResourceNameTypeEnum;
	groupId?: string;
	paymentId?: number;
	paymentStatus?: rcEnums.payment.PaymentStatus;
	externalAssign?: boolean;
	metadata?: ISeasonMetadata;
	createdAt?: Date;
	teamEntity?: RCTeam;
	userEntity?: RCUser;
	// payment?: PaymentsInstance;
}

export interface RCLeagueSeason {
	longDescription: string;
	inviteSendDate?: Date;
	seasonTiming?: 'current' | 'future' | 'past';
	registrationTiming?: 'early' | 'regular' | 'late' | 'close' | 'ended';
	startDate: string;
	endDate: string;
	id: number;
	activityTimes: RCActivityTime[];
	leagueId: number;
	name: string;
	canJoin?: RCCanJoinSeasonEnum;
	canJoinStatus?: RCSeasonCanJoinStatus;
	status?: RCLeagueSeasonStatusEnum;
	multipleDivision?: boolean;
	priceEarlySingle?: number;
	priceRegularSingle?: number;
	priceLateSingle?: number;
	priceEarlyTeam?: number;
	priceEarlyTeamMember?: number;
	priceRegularTeam?: number;
	priceLateTeam?: number;
	priceLateTeamMember?: number;
	priceEarlyGroup?: number;
	priceRegularTeamMember?: number;
	priceRegularGroup?: number;
	priceLateGroup?: number;
	earlyRegistrationEnds?: string;
	regularRegistrationEnds?: string;
	lateRegistrationEnds?: string;
	registrationOpen: Date;
	registrationStatus?: number;
	description?: string;
	addressName?: string;
	rounds?: RCSeasonRound[];
	// seasonLeague?: RCLeague;
	seasonVenues?: RCFacility[];
	seasonTeams?: RCSeasonTeam[];
	tournamentType?: boolean;
	// subSeasons?: RCLeagueSeason[];
	playoffType?: boolean;
	playerCount?: number;
	seasonDivisions?: RCSeasonDivision[];
	tournament?: {
		tournamentConfig?: unknown;
		name: string;
		id: number;
		tournamentType: 'singleElimination' | 'doubleElimination';
		seasonId: number;
	};
	seasonWindows?: RCVenueActivityItem[];
	scheduleStatus?: RCSeasonScheduleStatusEnum;
	rosterStatus?: RCSeasonRosterStatusEnum;
	connectedSeasonId?: number;
	connectedDivisionId?: number;
	metaData?: {
		playoff?: {
			teamsToChoose?: {
				amount?: number;
				orderType?: 'top' | 'bottom';
			};
		};
	};
	installments?: number;
	onlinePaymentEnabled?: boolean;
	allowCash?: boolean;
	paymentSettingStatus?: RCPaymentSettingStatus;
	canJoinTeamMember?: boolean;
	canJoinTeamMemberId?: number;
	parentSeason?: RCLeagueSeason;

	// set when loading the league
	organizationId: number;
	questionnaires: number[];
	startDateString: string;
	endDateString: string;
}

export interface RCLeague {
	id: number;
	sports: rcEnums.shared.SportsEnum[];
	maxAge: number;
	minAge: number;
	name: string;
	creator?: RCUser;
	startDate: Date;
	endDate: Date;
	isPublished: boolean;
	mainMedia?: {
		url: string;
	};
	media?: MediaInterface[];
	organizationId: number;
	logo?: RCMediaObject;
	address?: RCAddress;
	addressName?: string;
	description?: string;
	shortDescription?: string;
	longDescription?: string;
	waiverDoc?: string;
	leagueSeasons: RCLeagueSeason[];
	leagueDetails: IRCLeagueDetailData[];
	levelOfPlay: number[];
	following: boolean;
	timezone: string;
	bookingStateStatus: RCLeagueBookingStateEnum;
	teams?: RCTeam[];
	venues?: RCFacility[];
	shortUrl?: string;
	leagueType?: 'tournament' | 'league';
	webPortal?: RCWebPortal;
	sportConfigData?: {
		standingsView?: {
			pointsOrPercentageView: 'points' | 'percentage';
		};
	};

	questionnaireId?: number;
}

export interface LeagueSeason extends RCLeagueSeason {
	league: RCLeague;
	// facilities:
}
